// frontend/src/components/public/blocks/ImageHighlightBlock.tsx
import React from 'react';
import { Box, Typography } from '@mui/material';

interface ImageHighlightBlockProps {
  data: {
    backgroundImage: string;
    primary: string;              
    secondary?: string; 
    overlayOpacity?: number;
    position?: 'top' | 'center' | 'bottom';
    textAlign?: 'left' | 'center' | 'right';
    primaryTextColor?: 'white' | 'primary' | 'accent';
    secondaryTextColor?: 'white' | 'primary' | 'accent';
    primaryFontSize?: 'small' | 'medium' | 'large' | 'xlarge';  // ✅ NEW
    secondaryFontSize?: 'small' | 'medium' | 'large';            // ✅ NEW
  };
 theme?: {
    primaryColor: string;
    accentColor: string;
    watermark?: {
      url: string;
      position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
      opacity: number;
    };
  };
}

const ImageHighlightBlock: React.FC<ImageHighlightBlockProps> = ({ data, theme }) => {
  const {
    backgroundImage,
    primary,
    secondary,
    overlayOpacity = 0.5,
    position = 'center',
    textAlign = 'center',
    primaryTextColor = 'white',
    secondaryTextColor = 'white',
    primaryFontSize = 'large',      // ✅ NEW - defaults to large
    secondaryFontSize = 'medium'    // ✅ NEW - defaults to medium
  } = data;

  const primaryColor = theme?.primaryColor || '#000000';
  const accentColor = theme?.accentColor || '#FF0000';
  const watermark = theme?.watermark;


  // Helper function to get the actual color value
  const getTextColor = (colorChoice: 'white' | 'primary' | 'accent') => {
    switch (colorChoice) {
      case 'primary':
        return primaryColor;
      case 'accent':
        return accentColor;
      case 'white':
      default:
        return 'white';
    }
  };

  // ✅ NEW: Get primary font size based on selection
  const getPrimaryFontSize = () => {
    switch (primaryFontSize) {
      case 'small':
        return { xs: '2rem', sm: '2.5rem', md: '3rem', lg: '3.5rem' };
      case 'medium':
        return { xs: '2.5rem', sm: '3rem', md: '4rem', lg: '4.5rem' };
      case 'large':
        return { xs: '3rem', sm: '3.5rem', md: '5rem', lg: '6rem' };
      case 'xlarge':
        return { xs: '4rem', sm: '5rem', md: '7rem', lg: '8rem' };
      default:
        return { xs: '3rem', sm: '3.5rem', md: '5rem', lg: '6rem' };
    }
  };

  // ✅ NEW: Get secondary font size based on selection
  const getSecondaryFontSize = () => {
    switch (secondaryFontSize) {
      case 'small':
        return { xs: '0.875rem', sm: '1rem', md: '1.125rem' };
      case 'medium':
        return { xs: '1.125rem', sm: '1.375rem', md: '1.625rem' };
      case 'large':
        return { xs: '1.5rem', sm: '1.75rem', md: '2rem' };
      default:
        return { xs: '1.125rem', sm: '1.375rem', md: '1.625rem' };
    }
  };

  // Map position to vertical alignment
  const getVerticalAlign = () => {
    switch (position) {
      case 'top': return 'flex-start';
      case 'bottom': return 'flex-end';
      case 'center':
      default: return 'center';
    }
  };

  // Map textAlign to horizontal alignment
  const getHorizontalAlign = () => {
    switch (textAlign) {
      case 'left': return 'flex-start';
      case 'right': return 'flex-end';
      case 'center':
      default: return 'center';
    }
  };

  const primaryColorValue = getTextColor(primaryTextColor);
  const secondaryColorValue = getTextColor(secondaryTextColor);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: { xs: '50vh', md: '70vh' },
        display: 'flex',
        alignItems: getVerticalAlign(),
        justifyContent: getHorizontalAlign(),
        overflow: 'hidden',
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Dark Overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: 'black',
          opacity: overlayOpacity,
          zIndex: 1
        }}
      />

            {/* Watermark */}
            {watermark?.url && (
              <Box
                component="img"
                src={watermark.url}
                alt="Watermark"
                sx={{
                  position: 'absolute',
                  opacity: watermark.opacity ?? 0.15,
                  width: { xs: '80px', sm: '100px', md: '120px' },
                  pointerEvents: 'none',
                  zIndex: 1.5,
                  ...(watermark.position === 'top-left' && { top: 16, left: 16 }),
                  ...(watermark.position === 'top-right' && { top: 16, right: 16 }),
                  ...(watermark.position === 'bottom-left' && { bottom: 16, left: 16 }),
                  ...(watermark.position === 'bottom-right' && { bottom: 16, right: 16 })
                }}
              />
            )}

      {/* Content */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          textAlign: textAlign,
          px: { xs: 3, md: 6 },
          py: 4,
          maxWidth: '1200px',  // Increased from 900px for longer text
          width: '100%'
        }}
      >
        {primary && (
          <Typography
            variant="h2"
            sx={{
              fontSize: getPrimaryFontSize(),  // ✅ DYNAMIC FONT SIZE
              fontWeight: 800,
              mb: secondary ? 2 : 0,
              textShadow: '0 4px 16px rgba(0,0,0,0.7)',
              lineHeight: 1.1,
              position: 'relative',
              display: 'inline-block',
              color: `${primaryColorValue} !important`,
              '&::after': secondary ? {} : {
                content: '""',
                position: 'absolute',
                bottom: '-8px',
                left: textAlign === 'center' ? '50%' : textAlign === 'left' ? '0' : 'auto',
                right: textAlign === 'right' ? '0' : 'auto',
                transform: textAlign === 'center' ? 'translateX(-50%)' : 'none',
                width: '80px',
                height: '4px',
                bgcolor: accentColor,
                borderRadius: '2px'
              }
            }}
          >
            {primary}
          </Typography>
        )}

        {secondary && (
          <Typography
            variant="h5"
            sx={{
              fontSize: getSecondaryFontSize(),  // ✅ DYNAMIC FONT SIZE
              fontWeight: 400,
              textShadow: '0 2px 12px rgba(0,0,0,0.7)',
              opacity: 0.95,
              position: 'relative',
              display: 'inline-block',
              color: `${secondaryColorValue} !important`,
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: '-8px',
                left: textAlign === 'center' ? '50%' : textAlign === 'left' ? '0' : 'auto',
                right: textAlign === 'right' ? '0' : 'auto',
                transform: textAlign === 'center' ? 'translateX(-50%)' : 'none',
                width: '60px',
                height: '3px',
                bgcolor: accentColor,
                borderRadius: '2px'
              }
            }}
          >
            {secondary}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ImageHighlightBlock;